import { useAPIStore } from "@/stores/APIStore";
import { useUIStore } from "@/stores/UIStore";
import APIAction from "@/types/api/APIAction";
import ModulePermission from "@/types/modules/modulePermission";
import OrganizationModulePermission from "@/types/organization/OrganizationModulePermission";
import Permission from "@/types/roles/permission";
import PermissionBundle from "@/types/roles/permissionbundle";
import PermissionCategory from "@/types/roles/permissionCategory";
import PermissionMatrixRow from "@/types/roles/permissionMatrixRow";
import Role from "@/types/roles/role";
import { storeToRefs } from "pinia";
export * from "./permissionConstants";
export * from "./permissionShortcuts";

import { permissionMatrix } from "./permissionConstants";
/**

 * @param {PermissionBundle} bundle the name of the bundle
 * @returns {PermissionMatrixRow | null} the associated permission or null if it could not be found
 */
export const getPermissionDetails = (bundle : PermissionBundle) : PermissionMatrixRow | null => {
    return permissionMatrix.find(x => x.name == bundle)
}

/**
 * Converts the permission matrix to a request body
 * @returns a request body
 */
export const permissionMatrixToRequestBody = () => {
    const permissionsAdd = []
    let permissionsRemove = []

    permissionMatrix.forEach(bundle => {
        bundle.permissions.forEach(permission => {
            // if a permission is enabled and we have not yet added it, then add it to the add list
            // if a permission is disabled and we have not yet removed it, then add it to the remove list
            if(bundle.value && !permissionsAdd.includes(permission)) {
                permissionsAdd.push(permission)
            }else if(!bundle.value && !permissionsRemove.includes(permission)){
                permissionsRemove.push(permission)
            }
        })
    })

    // if a permission needs to be removed and a permission needs to be added, then add it instead of removing
    permissionsRemove = permissionsRemove.filter(perm => !permissionsAdd.includes(perm))

    // here we contruct the actual request body
    const rb = {
        add_permission: permissionsAdd,
        remove_permission: permissionsRemove,
    }

    return rb
}

/**
 * Gets the permissions that are assigned to a bundle
 * @param {PermissionBundle} bundle the bundle of which to get the permissions
 * @returns a list of permissions or a list ['not-allowed'] which
 *          indicates that this permission bundle does not exist
 */
export const getBundlePermissions = (bundle : PermissionBundle) => {
    try{
        return permissionMatrix.find(pm => pm.name == bundle).permissions
    }catch(e){
        return ['not-allowed']
    }
}

/**
 * Checks whether a bundle contains at least one of the provided permissions
 * @param {Permission[]} permissions the list of permissions to check for 
 * @param {PermissionBundle} bundle the bundle to check for includes
 * @returns {boolean} whether the given bundle contained at least one 
 *                    permission from the permissions list 
 */
export const hasPermission = (permissions : Permission[], bundle : PermissionBundle) : boolean => {
    return permissions.some(perm => getBundlePermissions(bundle).includes(perm))
}

/**
 * Gets a combined list of permission for a given list of roles since a user can have
 * multiple roles. This function can determine which permissions a user has
 * @param {Role[]} roles a list of role for which to combine the permissions
 * @returns {Permission[]} the combined list of permissions
 */
export const getPermissionFromRoles = (roles : Role[]) : Permission[] => {
    return roles.reduce((acc, role) => {
        return acc.concat(role.permissions)
    }, [])
}

/**
 * Gets a list of permission bundles that are assigned to a role.
 * This is usefol on the edit role page to determine which toggles are on/off
 * @param {Role} role the role for which to check the list of permission bundles
 * @returns {PermissionMatrixRow[]} the list of permission bundles that a role has
 */
export const getPermissionBundlesFromRole = (role : Role) : PermissionMatrixRow[] => {
    return permissionMatrix.filter(pm => pm.permissions.every(perm => role.permissions.includes(perm)))
}

/**
 * Toggles a permission bundle and then checks if that caused any other permission bundles
 * to also be enabled. This is possible when another role has a subset of permission from the just toggled role
 * or if the other role has a subset of permission of x other enabled roles combined with the just toggled role
 * @param {Permission[]} permissions the permissions that need to be enabled
 * @param {boolean} value indicates if the permissions need to be enabled or disabled
 */
export const recursivelyTogglePermissions = async (permissions : any[], value : boolean, bundles, refMap, includeSelf=false) => {
    let bundlesToAdd = []
    let bundlesToRemove = []

    const permissionsNames = bundles.map(b => b.value.name)


    permissionMatrix.forEach(bundle => {
        if(bundle.value != value && !permissionsNames.includes(bundle.name)){
            // if every permission of any bundle matches permissions from selected
            // then toggle this permission as well
            if(bundle.permissions.every(perm => permissions.includes(perm))){
                if(value) bundlesToAdd.push(refMap[bundle.name])
                else bundlesToRemove.push(refMap[bundle.name])
            }
            if(!value){
                // Only if deselecting if every permission of selected permission is part of another permission
                // Deselect that one as well (otherwise this permission will be auto re-enabled)
                if(permissions.every(perm => bundle.permissions.includes(perm))){
                    if(!bundlesToRemove.includes(bundle)) bundlesToRemove.push(refMap[bundle.name])
                }
            }
        }
    })

    let confirm = false
    const uiStore = useUIStore()
    if(bundlesToAdd.length > 0 || bundlesToRemove.length > 0) {
        if(bundlesToAdd.length > 0){
            uiStore.permissionsConfirmMode = "ADD"
            uiStore.permissionsConfirmPermissions = {
                self: bundles,
                extra: bundlesToAdd,
            }
        }else if(bundlesToRemove.length > 0){
            uiStore.permissionsConfirmMode = "REMOVE"
            uiStore.permissionsConfirmPermissions = {
                self: bundles,
                extra: bundlesToRemove,
            }
        }
        confirm = await uiStore.toggleConfirmPermissions()

        if(confirm){
            if(includeSelf && bundlesToAdd.length > 0) bundlesToAdd = [...bundles, ...bundlesToAdd]
            else if(includeSelf && bundlesToRemove.length > 0) bundlesToRemove = [...bundles, ...bundlesToRemove]
            bundlesToAdd.forEach(bundle => {
                bundle.value.value = true
            })
            bundlesToRemove.forEach(bundle => {
                bundle.value.value = false
            })
        }
    }else{
        confirm = true
        if(includeSelf){
            bundles.forEach(bundle => {
                bundle.value.value = value
            })
        }
    }

    return confirm
}

/**
 * Parses a list of action from a provided actions string eg. GET, POST
 * @param {string} actionsSTR the second segment of a module permission (split on :)
 * @returns {string[]} a list of actions parsed from the actions sting
 */
const parseActions = (actionsSTR : string) : string[] => {
    let actions = []
    const tmpActions = actionsSTR.split(",")

    // Add Actions
    tmpActions.forEach(tmpAction => {
        const allowedActions = [
            "LIST", "CREATE", "UPDATE", "DELETE",
        ]
        if(allowedActions.includes(tmpAction)){
            actions.push(tmpAction)
        }
    })

    // Shortcut for if middle is left empty
    if(actions.length == 0) actions = ["LIST"]

    return actions
}

/**
 * Parses an entire module permission and splits it up into the module name
 * module actions and module level. It also returns a success boolean
 * @param {ModulePermission} modulePermission the module permission to be parsed
 * @returns the module name, module actions, module level and success status
 */
const parseModulePermission = (modulePermission : ModulePermission) => {
    const input = modulePermission.split(":")

    let moduleName = ""
    let actions = ["LIST"]
    let moduleLevel = 1 //default value
    let success = true

    if(input.length > 0) moduleName = input[0]
    if(input.length > 1) actions = parseActions(input[1])
    if(input.length == 3) moduleLevel = parseInt(input[2])      

    if(moduleName == "") success = false

    return { moduleName, actions, moduleLevel, success }
}

/**
 * Checks if a string format module permission and object format module permission match
 * the object module permission actions should be equal or a superset of the module permission actions
 * @param {ModulePermission} modulePermission the string format module permission
 * @param {OrganizationModulePermission} organizationModulePermission the object format module permission
 * @returns {boolean} whether or not the permission match
 */
const modulePermissionMatch = (modulePermission : ModulePermission, organizationModulePermission : OrganizationModulePermission) : boolean => {
    const {moduleName, actions, moduleLevel, success} = parseModulePermission(modulePermission)
    if(!success) return false

    if(moduleName == organizationModulePermission.module && moduleLevel <= organizationModulePermission.permission_level){
        return actions.every(action => organizationModulePermission.module_actions.includes(action as APIAction))
    }
    
    return false
}

/**
 * Checks if the current organization has a given module permission
 * 
 * modulePermission = MODULE_NAME:ACTION1,ACTION2:LEVEL 
 * eg.             -> LOCATION_TYPE:LIST,CREATE:1
 *
 * shorthands: 
 * MODULE_NAME:ACTION1,ACTION2 = MODULE_NAME:ACTION1,ACTION2:1
 * MODULE_NAME = MODULE_NAME:LIST:1
 * MODULE_NAME::2 = MODULE_NAME:LIST:2
 * @param modulePermission 
 * @returns 
 */
export const hasOrganizationModulePermission = (modulePermission : ModulePermission) => {
    const APIStore = useAPIStore()
    return (APIStore.organizationModulePermissions || []).some(x => modulePermissionMatch(modulePermission, x))
}

/**
 * Checks if the current organization has every module permission that is provided to this function
 * @param {ModulePermission[]} modulePermissions the list of module permissions to be checked
 * @returns {boolean} whether or not the organization has all the module permission provided
 */
export const hasOrganizationModulePermissions = (modulePermissions : ModulePermission[]) => {
    return modulePermissions.every(perm => hasOrganizationModulePermission(perm))
}

/**
 * Checks if this organization has any of the provided required module permissions.
 * The inner array requires all and the outer array requires some 
 * eg. [[A && B] || [C]]
 * @param {ModulePermission[][]} modulePermissions the list of module permission requirements
 * @returns {boolean} whether or not the organization has at least one of the module permission requirements
 */
export const hasAnyOrganizationModulePermissions = (modulePermissions : ModulePermission[][]) : boolean => {
    return modulePermissions.some(perms => hasOrganizationModulePermissions(perms))
}

/**
 * Checks whether the active user has a given permission bundle
 * @param {PermissionBundle} perm the permission bundle to be checked
 * @returns {boolean} whether or not the active user has the provided permission bundle
 */
export const hasUserPermission = (perm : PermissionBundle) : boolean => {
    const APIStore = useAPIStore()
    const { userPermissions } = storeToRefs(APIStore)
    return getBundlePermissions(perm).every(p => userPermissions.value.includes(p))
}

/**
 * Checks whether or not the active user has all the permission bundles provided
 * @param {PermissionBundle[]} permissions the list of permission bundles to check agains
 * @returns {boolean} whether or not the active user has all these permission bundles
 */
export const hasUserPermissions = (permissions : PermissionBundle[]) : boolean => {
    return permissions.every(perm => hasUserPermission(perm))
}

/**
 * Checks if the active user has any of the provided required permission bundles.
 * The inner array requires all and the outer array requires some 
 * eg. [[A && B] || [C]]
 * @param permissions the list of permission bundle requirements
 * @returns whether or not the active user has at least one of the permission bundle requirements
 */
export const hasAnyUserPermissions = (permissions : PermissionBundle[][]) => {
    return permissions.some(perms => hasUserPermissions(perms))
}
